import { useState, useRef, useEffect } from 'react';
import { Play, Pause, Download, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { MediaInfo } from '../types';
import { useMediaUrl, fetchMediaWithAuth } from '../hooks';

interface AudioContentProps {
  media: MediaInfo;
  chatId: number;
  messageId: number;
  isOutgoing?: boolean;
  className?: string;
}

/**
 * Компонент для отображения аудио файлов (музыка)
 */
export function AudioContent({ media, chatId, messageId, isOutgoing, className }: AudioContentProps) {
  const { url: mediaUrl, isLoading, error } = useMediaUrl(chatId, messageId);
  const audioRef = useRef<HTMLAudioElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(media.duration || 0);

  // Reset state on message change
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(media.duration || 0);
  }, [messageId, media.duration]);

  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (audioRef.current && duration > 0) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const progress = x / rect.width;
      audioRef.current.currentTime = progress * duration;
    }
  };

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const blob = await fetchMediaWithAuth(chatId, messageId);
    if (blob) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = media.fileName || `${media.title || 'audio'}.${media.mimeType?.split('/')[1] || 'mp3'}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className={cn('py-1 min-w-[240px]', className)}>
      {/* Hidden audio element */}
      {mediaUrl && !error && (
        <audio
          ref={audioRef}
          src={mediaUrl}
          onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
          onLoadedMetadata={() => setDuration(audioRef.current?.duration || media.duration || 0)}
          onEnded={() => setIsPlaying(false)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        />
      )}

      <div className="flex items-center gap-3">
        {/* Play button / Album art */}
        <button
          onClick={handlePlayPause}
          disabled={isLoading || !!error}
          className={cn(
            'w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors',
            isOutgoing
              ? 'bg-white/20 hover:bg-white/30 text-white'
              : 'bg-primary/10 hover:bg-primary/20 text-primary',
            (isLoading || error) && 'opacity-50 cursor-not-allowed'
          )}
        >
          {isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (isPlaying ? (
            <Pause className="h-5 w-5" />
          ) : (
            <Play className="h-5 w-5 ml-0.5" />
          ))}
        </button>

        {/* Track info */}
        <div className="flex-1 min-w-0">
          {/* Title and performer */}
          <p className="text-sm font-medium truncate">
            {media.title || media.fileName || 'Аудио'}
          </p>
          {media.performer && (
            <p
              className={cn(
                'text-xs truncate',
                isOutgoing ? 'text-white/70' : 'text-muted-foreground'
              )}
            >
              {media.performer}
            </p>
          )}

          {/* Progress bar */}
          <div
            onClick={handleSeek}
            className={cn(
              'h-1 mt-2 rounded-full cursor-pointer',
              isOutgoing ? 'bg-white/20' : 'bg-muted'
            )}
          >
            <div
              className={cn(
                'h-full rounded-full transition-all',
                isOutgoing ? 'bg-white' : 'bg-primary'
              )}
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Time */}
          <div className="flex justify-between mt-1">
            <span
              className={cn(
                'text-[10px]',
                isOutgoing ? 'text-white/60' : 'text-muted-foreground'
              )}
            >
              {formatTime(currentTime)}
            </span>
            <span
              className={cn(
                'text-[10px]',
                isOutgoing ? 'text-white/60' : 'text-muted-foreground'
              )}
            >
              {formatTime(duration)}
            </span>
          </div>
        </div>

        {/* Download button */}
        <button
          onClick={handleDownload}
          className={cn(
            'p-2 rounded-full transition-colors flex-shrink-0',
            isOutgoing ? 'hover:bg-white/20' : 'hover:bg-muted'
          )}
          title="Скачать"
        >
          <Download className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
